/**
 * S15 — Compliance Centre Detail Drawer (right-side, 720 px).
 *
 * Shows the full picture for one framework:
 *   · Coverage ring + meta (jurisdiction, last evidence, auditor)
 *   · Summary
 *   · Domain breakdown (bars)
 *   · Controls table (id · name · domain · status · evidence · linked policies)
 *   · Open gaps (with owner + due date)
 *   · Recent evidence (signed reports for this framework)
 *   · Linked policies (clickable → catalogue) and linked agents
 *
 * Read-only — authoring of controls / evidence collection is deferred.
 */
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  X, MapPin, CalendarClock, UserCheck, Layers, ListChecks, AlertTriangle, FileSignature, ShieldCheck, ArrowUpRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  CoverageRing, ControlStatusPill, DomainBar, GapBadge,
} from "./components/CompliancePrimitives";

export default function FrameworkDetailDrawer({ framework, onClose, policies, agents, reports }) {
  useEffect(() => {
    if (!framework) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [framework, onClose]);

  if (!framework) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="framework-detail-drawer" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid="framework-drawer-backdrop"
      />
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[720px] bg-raised shadow-e4 border-l border-border-subtle flex flex-col"
        style={{ animation: "slide-in-right 220ms ease-out" }}
      >
        <DrawerHeader framework={framework} onClose={onClose} />
        <DrawerBody framework={framework} policies={policies} agents={agents} reports={reports} />
        <DrawerFooter onClose={onClose} />
      </aside>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function DrawerHeader({ framework: f, onClose }) {
  return (
    <div className="border-b border-border-subtle px-5 pt-4 pb-4" data-testid="framework-drawer-header">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-meta font-mono text-ink-secondary tabular-nums">{f.id}</span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-inset text-[10px] font-bold tracking-[0.06em] uppercase text-ink-secondary">
              <MapPin size={10} /> {f.jurisdiction}
            </span>
            <GapBadge count={(f.gaps || []).length} />
          </div>
          <h2 className="mt-1 text-h2 text-ink-primary" data-testid="framework-drawer-title">{f.name}</h2>
          <p className="mt-1 text-meta text-ink-secondary inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> Last evidence {f.lastEvidenceTs}</span>
            <span className="inline-flex items-center gap-1"><UserCheck size={11} /> Auditor {f.lastAuditor}</span>
          </p>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          <CoverageRing value={f.coverage} size={88} stroke={7} label="coverage" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset transition-colors"
            data-testid="framework-drawer-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <StatTile label="Implemented" value={f.controlsMet} tone="low" />
        <StatTile label="Partial"     value={f.controlsPartial} tone="med" />
        <StatTile label="Gap"         value={f.controlsGap} tone="high" />
      </div>
    </div>
  );
}

function DrawerBody({ framework: f, policies, agents, reports }) {
  const policyMap = useMemo(() => new Map(policies.map((p) => [p.id, p])), [policies]);
  const linkedPolicies = useMemo(
    () => policies.filter((p) => (p.frameworks || []).includes(f.name)),
    [policies, f.name],
  );
  const linkedAgents = useMemo(
    () => agents.filter((a) => a.framework === f.name).slice(0, 12),
    [agents, f.name],
  );
  const linkedReports = useMemo(
    () => reports.filter((r) => r.framework === f.name),
    [reports, f.name],
  );

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" data-testid="framework-drawer-body">
      <Section icon={ShieldCheck} title="Summary">
        <p className="text-[15px] text-ink-primary leading-relaxed" data-testid="framework-drawer-summary">
          {f.summary}
        </p>
      </Section>

      <Section icon={Layers} title="Domains">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="framework-drawer-domains">
          {(f.domains || []).map((d) => (
            <DomainBar key={d.id} name={d.name} met={d.controlsMet} total={d.controlsTotal} />
          ))}
        </div>
      </Section>

      <Section icon={ListChecks} title="Controls">
        <ControlsTable controls={f.controls} policyMap={policyMap} />
      </Section>

      <Section icon={AlertTriangle} title="Open Gaps">
        <GapList gaps={f.gaps} />
      </Section>

      <Section icon={FileSignature} title="Recent Evidence">
        <ReportsList reports={linkedReports} />
      </Section>

      <Section icon={ShieldCheck} title="Linked Policies">
        <LinkedPolicies policies={linkedPolicies} />
      </Section>

      <Section icon={ShieldCheck} title="Linked Agents">
        <LinkedAgents agents={linkedAgents} />
      </Section>
    </div>
  );
}

function DrawerFooter({ onClose }) {
  return (
    <div className="border-t border-border-subtle px-5 py-3 bg-inset/30 flex items-center gap-2" data-testid="framework-drawer-footer">
      <button
        type="button"
        onClick={onClose}
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors"
        data-testid="framework-drawer-action-close"
      >
        Close
      </button>
      <span className="flex-1" />
      <span className="inline-flex items-center gap-1.5 text-meta text-ink-disabled">
        <FileSignature size={12} /> Custom report builder available in a later wave
      </span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function StatTile({ label, value, tone }) {
  const toneClass =
    tone === "low" ? "text-risk-low" : tone === "med" ? "text-risk-med" : tone === "high" ? "text-risk-high" : "text-ink-primary";
  return (
    <div className="rounded-md bg-inset/60 px-2 py-2">
      <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-ink-secondary">{label}</p>
      <p className={cn("mt-0.5 text-lg font-semibold tabular-nums leading-none", toneClass)}>{value}</p>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={13} className="text-tralenex-700" />
        <p className="eyebrow">{title}</p>
      </div>
      <div className="text-sm">{children}</div>
    </section>
  );
}

function ControlsTable({ controls, policyMap }) {
  if (!controls || controls.length === 0) {
    return <p className="text-meta text-ink-secondary">No controls mapped yet.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border-subtle/60">
      <table className="w-full text-sm">
        <thead className="bg-inset/40 border-b border-border-subtle">
          <tr>
            {["Control", "Status", "Evidence", "Linked policies"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle/60">
          {controls.map((c) => (
            <tr key={c.id} data-testid={`framework-control-${c.id}`}>
              <td className="px-3 py-2 align-top">
                <p className="font-mono text-meta text-ink-secondary tabular-nums">{c.id}</p>
                <p className="text-ink-primary leading-snug">{c.name}</p>
              </td>
              <td className="px-3 py-2 align-top"><ControlStatusPill status={c.status} /></td>
              <td className="px-3 py-2 align-top">
                <span className="inline-flex items-center gap-1 text-meta tabular-nums">
                  <span className="font-semibold text-ink-primary">{c.evidenceCount}</span>
                  <span className="text-ink-disabled">artefacts</span>
                </span>
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-wrap gap-1">
                  {(c.policyIds || []).length === 0 && (
                    <span className="text-meta text-ink-disabled italic">—</span>
                  )}
                  {(c.policyIds || []).map((pid) => (
                    <Link
                      key={pid}
                      to="/govern/policies"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-tralenex-50 text-[11px] text-tralenex-900 font-medium hover:bg-tralenex-100"
                      data-testid={`framework-control-policy-${pid}`}
                    >
                      {policyMap.get(pid)?.id || pid}
                    </Link>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GapList({ gaps }) {
  if (!gaps || gaps.length === 0) {
    return (
      <p className="text-meta text-risk-low" data-testid="framework-drawer-no-gaps">
        No open gaps — framework is fully evidenced for this audit window.
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="framework-drawer-gaps">
      {gaps.map((g) => (
        <li
          key={g.id}
          className="rounded-md border border-risk-high/30 bg-risk-high/5 p-3 flex items-start gap-3"
        >
          <AlertTriangle size={14} className="text-risk-high mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-ink-primary leading-relaxed">{g.title}</p>
            <p className="text-meta text-ink-secondary mt-0.5">
              <span className="font-mono">{g.id}</span> · owner {g.owner} · due {g.dueBy}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ReportsList({ reports }) {
  if (!reports || reports.length === 0) {
    return <p className="text-meta text-ink-secondary">No framework-specific reports generated yet.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="framework-drawer-reports">
      {reports.map((r) => (
        <li
          key={r.id}
          className="flex items-center gap-3 rounded-md border border-border-subtle/60 px-3 py-2"
        >
          <FileSignature size={14} className="text-tralenex-700 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-ink-primary truncate">{r.name}</p>
            <p className="text-meta text-ink-secondary">
              {r.generatedAt} · {r.generatedBy} · {r.sizeMb} MB · signed
            </p>
          </div>
          <span className="text-meta font-mono text-ink-disabled tabular-nums">{r.id}</span>
        </li>
      ))}
    </ul>
  );
}

function LinkedPolicies({ policies }) {
  if (!policies || policies.length === 0) {
    return <p className="text-meta text-ink-secondary">No policies mapped to this framework.</p>;
  }
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="framework-drawer-linked-policies">
      {policies.map((p) => (
        <li key={p.id}>
          <Link
            to="/govern/policies"
            className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md border border-border-subtle/60 hover:border-tralenex-300 hover:bg-tralenex-50/40 transition-colors"
            data-testid={`framework-drawer-policy-${p.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm text-ink-primary truncate">{p.name}</p>
              <p className="text-meta text-ink-secondary font-mono tabular-nums">{p.id}</p>
            </div>
            <ArrowUpRight size={12} className="text-ink-disabled" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function LinkedAgents({ agents }) {
  if (!agents || agents.length === 0) {
    return <p className="text-meta text-ink-secondary">No governed agents currently mapped to this framework.</p>;
  }
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="framework-drawer-linked-agents">
      {agents.map((a) => (
        <li key={a.id}>
          <Link
            to={`/govern/agents/${a.id}`}
            className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md border border-border-subtle/60 hover:border-tralenex-300 hover:bg-tralenex-50/40 transition-colors"
            data-testid={`framework-drawer-agent-${a.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm text-ink-primary truncate">{a.name}</p>
              <p className="text-meta text-ink-secondary font-mono tabular-nums">{a.id} · {a.department}</p>
            </div>
            <ArrowUpRight size={12} className="text-ink-disabled" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
